import { useCallback } from "react";

import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { useProfileStore } from "~/store/profileStore";
import { Client } from "~/types/client";

// Normalize values for comparison - treats null, undefined, and empty string as equivalent
function normalizeValue(val: unknown): string {
  if (val === null || val === undefined || val === "") return "";
  return String(val).trim();
}

// Helper function to detect if client data has changed
// Only compares fields that are present in newData
function hasChanges(
  existing: Record<string, unknown>,
  newData: Record<string, unknown>
): boolean {
  return Object.keys(newData).some((key) => {
    const oldVal = normalizeValue(existing[key]);
    const newVal = normalizeValue(newData[key]);
    return oldVal !== newVal;
  });
}

export const useClientImport = () => {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();

  // Import clients from parsed data - BULK UPDATE ONLY MODE with change detection
  const importClients = useCallback(
    async (clientsData: Partial<Client>[]) => {
      try {
        // Validate that we have a valid user ID
        if (!userId) {
          throw new Error(
            "Usuario no autenticado. Por favor, inicie sesión nuevamente."
          );
        }

        console.log(
          `Starting BULK UPDATE-ONLY import with userId: ${userId}, ${clientsData.length} clients`
        );

        // Get existing clients with ALL comparable fields for change detection
        const { data: existingClients, error: fetchError } = await supabase
          .from("clients")
          .select(
            "id, tax_id, name, email, phone_number, insured_amount, trailing_payment_days, risk_checked, assigned_seller, sales_condition_id, internal_notes, collection_contact_person, collection_contact_email, collection_contact_phone, sales_channel, auxiliary_type"
          )
          .is("deleted_at", null);

        if (fetchError) throw fetchError;

        // Create maps for matching (with full client data for comparison)
        const existingByTaxId = new Map<string, Record<string, unknown>>();
        const existingById = new Map<string, Record<string, unknown>>();

        existingClients?.forEach((client) => {
          existingById.set(client.id, client);
          if (client.tax_id) {
            existingByTaxId.set(client.tax_id, client);
          }
        });

        // Prepare bulk update data with change detection
        const updateRecords: Array<{ id: string; [key: string]: unknown }> = [];
        const skippedClients: Array<{ name: string; taxId: string }> = [];
        const unchangedClients: Array<{ name: string; taxId: string }> = [];

        for (const current of clientsData) {
          let targetId: string | null = null;
          let existingClient: Record<string, unknown> | null = null;

          if (current.id && existingById.has(current.id)) {
            targetId = current.id;
            existingClient = existingById.get(current.id) || null;
          } else if (current.taxId && existingByTaxId.has(current.taxId)) {
            existingClient = existingByTaxId.get(current.taxId) || null;
            targetId = existingClient?.id as string;
          }

          if (targetId && existingClient) {
            // Prepare update data - only include fields that have actual values in the import
            const updateData: Record<string, unknown> = {};

            // Core fields - always include if present
            if (current.name !== undefined) updateData.name = current.name;
            if (current.taxId !== undefined) updateData.tax_id = current.taxId;

            // Optional text fields - only include if explicitly provided (not undefined)
            if (current.email !== undefined)
              updateData.email = current.email || "";
            if (current.phone !== undefined)
              updateData.phone_number = current.phone || "";
            if (current.internal_notes !== undefined)
              updateData.internal_notes = current.internal_notes || "";

            // Numeric fields - only include if explicitly provided
            if (current.insuredAmount !== undefined)
              updateData.insured_amount = current.insuredAmount || 0;
            if (current.trailingPaymentDays !== undefined)
              updateData.trailing_payment_days =
                current.trailingPaymentDays || 0;

            // Boolean fields - only include if explicitly provided
            if (current.risk_checked !== undefined)
              updateData.risk_checked = current.risk_checked || false;

            // Foreign key fields - only include if explicitly provided (these are often missing in exports)
            if (current.sellerId !== undefined)
              updateData.assigned_seller = current.sellerId || null;
            if (current.salesConditionId !== undefined)
              updateData.sales_condition_id = current.salesConditionId || null;

            // Collection contact fields - only include if explicitly provided
            const currentRecord = current as Record<string, unknown>;
            if (currentRecord.collection_contact_person !== undefined) {
              updateData.collection_contact_person =
                currentRecord.collection_contact_person || "";
            }
            if (currentRecord.collection_contact_email !== undefined) {
              updateData.collection_contact_email =
                currentRecord.collection_contact_email || "";
            }
            if (currentRecord.collection_contact_phone !== undefined) {
              updateData.collection_contact_phone =
                currentRecord.collection_contact_phone || "";
            }

            // Additional foreign keys - only include if explicitly provided
            if (currentRecord.sales_channel !== undefined)
              updateData.sales_channel = currentRecord.sales_channel || null;
            if (currentRecord.auxiliary_type !== undefined)
              updateData.auxiliary_type = currentRecord.auxiliary_type || null;

            // Only add to update records if there are fields to update and something actually changed
            if (
              Object.keys(updateData).length > 0 &&
              hasChanges(existingClient, updateData)
            ) {
              updateRecords.push({
                id: targetId,
                ...updateData,
              });
            } else {
              unchangedClients.push({
                name: current.name || "Sin nombre",
                taxId: current.taxId || "Sin Tax ID",
              });
            }
          } else {
            // Client not found - skip
            skippedClients.push({
              name: current.name || "Sin nombre",
              taxId: current.taxId || "Sin Tax ID",
            });
          }
        }

        console.log(
          `📊 Change detection: ${updateRecords.length} changed, ${unchangedClients.length} unchanged, ${skippedClients.length} not found`
        );

        let updatedCount = 0;
        const errors: string[] = [];

        // Only process if there are actual changes
        if (updateRecords.length > 0) {
          // Use smaller batch size (10) with parallel execution within batch
          // to avoid deadlocks from triggers while still being faster than sequential
          const BATCH_SIZE = 10;
          const batches: Array<Array<{ id: string; [key: string]: unknown }>> =
            [];
          for (let i = 0; i < updateRecords.length; i += BATCH_SIZE) {
            batches.push(updateRecords.slice(i, i + BATCH_SIZE));
          }

          console.log(
            `📦 Processing ${batches.length} batches (${BATCH_SIZE} clients each)...`
          );

          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];

            // Execute updates in this batch in parallel
            const updatePromises = batch.map((client) => {
              const { id, ...updateData } = client;
              return supabase.from("clients").update(updateData).eq("id", id);
            });

            const results = await Promise.all(updatePromises);

            // Count successes and collect errors
            results.forEach((result, idx) => {
              if (result.error) {
                console.error(
                  `❌ Failed to update client ${batch[idx].id}:`,
                  result.error
                );
                errors.push(`Client ${batch[idx].id}: ${result.error.message}`);
              } else {
                updatedCount++;
              }
            });

            // Log progress every 10 batches
            if ((i + 1) % 10 === 0 || i === batches.length - 1) {
              console.log(
                `✅ Progress: ${updatedCount}/${updateRecords.length} clients updated`
              );
            }
          }
        }

        // Build feedback message
        const skippedCount = skippedClients.length;
        const unchangedCount = unchangedClients.length;
        let message = `${updatedCount} clientes actualizados`;
        if (unchangedCount > 0) {
          message += `, ${unchangedCount} sin cambios`;
        }
        if (skippedCount > 0) {
          message += `, ${skippedCount} no encontrados`;
        }

        // Show detailed skipped clients in console
        if (skippedClients.length > 0) {
          console.error("🚨 CLIENTES NO ENCONTRADOS:");
          skippedClients.forEach((client) =>
            console.error(`- ${client.name} (Tax ID: ${client.taxId})`)
          );
        }

        // Show batch errors if any
        if (errors.length > 0) {
          console.error("🚨 ERRORES EN LOTES:");
          errors.forEach((error) => console.error(`- ${error}`));
        }

        // Determine toast variant and title based on results
        let title = "Importación completada";
        if (updatedCount === 0 && unchangedCount > 0) {
          title = "Sin cambios detectados";
        } else if (skippedCount > 0) {
          title = "Importación completada con omisiones";
        }

        toast({
          title,
          description: message,
          variant:
            skippedCount > 0 || errors.length > 0 ? "default" : "default",
        });

        return clientsData || [];
      } catch (err) {
        console.error("Error importing clients:", err);
        toast({
          title: "Error al importar clientes",
          description:
            "No se pudieron importar los clientes. Por favor, intente de nuevo.",
          variant: "destructive",
        });
        return [];
      }
    },
    [toast, userId]
  );

  return {
    importClients,
  };
};
